import React from 'react';
import ReactDOM from 'react-dom';
import { AppProvider } from "@shopify/polaris";
import { Provider } from "@shopify/app-bridge-react";
import createApp from "@shopify/app-bridge";
import { Redirect } from "@shopify/app-bridge/actions";
import '@shopify/polaris/styles.css';
import "./styles/app.scss";
import Env from "./Env";
import CsI18n from "./shared/csI18n";
import spfEn from "@shopify/polaris/locales/en.json";
import spfFr from "@shopify/polaris/locales/fr.json";
import spfEs from "@shopify/polaris/locales/es.json";
import spfIt from "@shopify/polaris/locales/it.json";
import spfDe from "@shopify/polaris/locales/de.json";
import CsLocalStorage from "./CsLocalStorage";
import LegalAppBridge from "./LegalAppBridge";
import Legal from "./components/Legal";
import CsConstant from "./CsConstant";
import moment from "moment";
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";

const transKey = 'cs_shopify_legal_translation:';

const debugMode = !Env.isProdMode();

// The app runs in the admin iframe, where assigning window.top.location is blocked unless the
// browser sees a user gesture. App Bridge owns the top frame, so it is what moves the merchant to
// the Shopify billing page, with the raw assignment kept as a fallback for non embedded loads.
const redirectToBilling = (url) => {
  try {
    const app = createApp({
      apiKey: Env.getApiKey(),
      host: Env.getHost(),
      forceRedirect: false,
    });
    Redirect.create(app).dispatch(Redirect.Action.REMOTE, url);
  } catch (err) {
    Env.log('App Bridge redirect unavailable, falling back', err);
    window.top.location.href = url;
  }
};

const storeName = Env.getStoreName();
if (storeName) {
  fetch(Env.getBackendURL() + 'billing/check?shop=' + encodeURIComponent(storeName), {
    method: 'GET',
    headers: {
        'Content-Type': 'application/json',
        'Accept': '*/*'
    }
  }).then(response => {
    return response.json();
  }).then(response => {
    if (response.success) {
      return;
    }

    if (response.confirmation_url) {
      redirectToBilling(response.confirmation_url);
      return;
    }

    // No subscription and no link to offer: the store has to be reinstalled, the backend logged why
    Env.log('Billing check returned no confirmation URL', response);
  }).catch(err => {
    Env.log('Billing check failed', err);
  });
} else {
  Env.log('No shop in the URL, skipping the billing check');
}

const getSpfI18n = (userLang) => {
  switch (userLang) {
    case 'fr':
      return spfFr;
    case 'es':
      return spfEs;
    case 'it':
      return spfIt;
    case 'de':
      return spfDe;
    default:
      return spfEn;
  }
}
const getUserLang = (locale) => {
  switch (locale) {
    case 'fr':
    case 'fr-FR':
      return 'fr';
    case 'es':
    case 'es-ES':
      return 'es';
    case 'it':
    case 'it-IT':
      return 'it';
    case 'de':
    case 'de-DE':
      return 'de';
    default:
      return 'en';
  }
}

// Suppress all log, debug in production
if (!debugMode) {
  // Sentry integration
  Sentry.init({
    dsn: CsConstant.SENTRY_DNS,
    integrations: [
      new Integrations.BrowserTracing(),
    ],

    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 1.0,
  });

  let nulFunc = () => {
    // Nothing
  };
  window['console']['log'] = nulFunc;
  window['console']['info'] = nulFunc;
  window['console']['error'] = nulFunc;
  window['console']['warn'] = nulFunc;
}

LegalAppBridge.init(Env, CsI18n)
  .then(locale => {
    const lang = getUserLang(locale);
    Env.log('1. Detect language:', locale, lang);

    // Locale done, continue...
    return CsI18n.init(
      lang,
      debugMode,
      Env.log,
      () => {
        Env.log('Translation: Get cache');
        const lastUpdate = moment.utc(CsConstant.TRANSLATION_LAST_UPDATE, 'YYYY-MM-DD hh:mm:ss').valueOf();
        return CsLocalStorage.loadStorageByExpiredTime(transKey + lang, CsConstant.TRANSLATION_EXPIRE_TIME, lastUpdate);
      },
      (defaultKey, data) => {
        Env.log('Translation: Set cache', data.length);
        CsLocalStorage.saveStorageWithTimestamp(transKey + lang, data);
      },
      () => {
        Env.log('Translation: Remove cache');
        CsLocalStorage.removeStorage(transKey + lang);
      },
      Env.getRefreshTranslation()
    );
  })
  .then(lang => {
    Env.log('2. Init app:', lang);
    ReactDOM.render(
      <React.StrictMode>
        <AppProvider i18n={getSpfI18n(lang)}>
          <Provider config={LegalAppBridge.getAppBridgeConfig()}>
            <Legal lang={lang} />
          </Provider>
        </AppProvider>
      </React.StrictMode>,
      document.getElementById('root')
    );
  })
  .catch(error => {
    Env.log('Init app error!')
    // If missing API key | shopOrigin, fail silently.
    if (LegalAppBridge.isValidApp()) {
      throw new Error(error);
    }
  });
